// @givmo/compliance — compliance filing-register decode + display helpers (CCM-WEB1).
//
// ══ THE ONE RULE THIS FILE EXISTS TO ENFORCE ═══════════════════════════════════════════════════
//
// The register renders a RECORD, never a status. The backend's field names are built to make that
// hard to get wrong — `filing_record_state` and `candidate_state` name what was recorded rather than
// what is true of the organization, and the `_filed` suffix rides every receipt-backed value — and
// the decode is where a client either keeps that or throws it away.
//
// ⚠ **`registered_filed` does NOT render as "Registered".** The backend's own entity docstring names
// this as the failure it is guarding against: "a client that renders `registered_filed` as
// 'Registered' has re-introduced the conclusion the shape exists to avoid". Every label below names
// what was RECORDED — "Registration filing recorded" — and never what the organization's legal
// standing is. Nothing in this module reaches a conclusion about an organization's standing in any
// jurisdiction, because nothing on the wire says so and no arrangement of what the wire does say
// adds up to it.
//
// The vocabulary is the `filing_register_*` family throughout; the status-shaped and stance-shaped
// alternatives are banned outright as wire names and as code identifiers. Their exact spellings are
// enumerated in `compliance-copy-law.test.ts` — the one file in this feature permitted to write them
// down, because the scan reads this source and naming them here would red the build. (The backend
// hit the same collision and resolved it the same way: its banned-word matrix lives in a test file
// no lint scope scans.)
//
// ══ TOLERANT READER ════════════════════════════════════════════════════════════════════════════
//
// Generated operation and schema types now cover these shapes, but they are compile-time claims about
// the wire rather than runtime validation. Every field is therefore still read off `unknown` and
// coerced defensively. Nothing here throws: an unreadable row degrades to the least-asserting render,
// and an unreadable value NEVER resolves towards the more confident answer.

import { extractList } from '@givmo/api-client/list';
import {
  firstErrorCode,
  isGivmoError,
  type ComplianceProfileParameterRecord,
} from '@givmo/api-client';

// ── Wire vocabularies ──────────────────────────────────────────────────────────────────────────

/** Where a derived candidate stands with the organization. A `proposed` row is inert — no deadline,
 *  no occurrence, no alert exists until the organization affirmatively confirms it. */
export type CandidateState = 'proposed' | 'confirmed' | 'dismissed';

/** What the organization RECORDED about a filing. Never a status. */
export type FilingRecordState =
  | 'none'
  | 'registered_filed'
  | 'exempt_filed'
  | 'renewed_filed'
  | 'window_closed'
  | 'submission_recorded';

/** Whether an imported registration arrived with the confirmation the state issued. */
export type ImportArtifactState = 'recorded' | 'requested';

/** WHICH recorded fact put a candidate on the list — the segregation marker. */
export type CandidateOrigin =
  | 'recorded_jurisdiction'
  | 'derived_nationwide_online'
  | 'organization_recorded_import';

const FILING_RECORD_STATES: readonly string[] = [
  'none',
  'registered_filed',
  'exempt_filed',
  'renewed_filed',
  'window_closed',
  'submission_recorded',
];

/**
 * The three receipt-backed states.
 *
 * ⚠ `window_closed` and `submission_recorded` are BOTH absent, for the same reason, and it is not an
 * omission: neither is evidence a state issued anything. One records that a window passed with
 * nothing filed; the other records that the organization submitted something. Treating either as a
 * filing would make the receipt rule mean "a date was typed" rather than "a state confirmed", which
 * is the whole distinction the register rests on.
 */
const RECEIPT_BACKED_FILING_STATES: readonly FilingRecordState[] = [
  'registered_filed',
  'exempt_filed',
  'renewed_filed',
];

// ── Coercion primitives ────────────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** A nullable string field: absent, null and unparseable all collapse to undefined, never to ''. */
function optionalStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function int(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * A boolean field, read STRICTLY.
 *
 * `=== true` rather than truthiness: a string, number or object arriving on a boolean is a shape this
 * build does not understand, and the safe reading of "I do not understand this" is the restrictive
 * one for a capability — never the permissive one.
 */
function bool(value: unknown): boolean {
  return value === true;
}

/** A nullable boolean field: `true` or `false` as served; null, absent and any other shape are `null`. */
function nullableBool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

// ── Rule provenance — the citation chain every rendered rule fact carries ───────────────────────

/**
 * The provenance triplet the backend attaches to every rule it renders: what the rule cites, where it
 * was read, and WHEN the source was last checked.
 *
 * The date is not decoration. It is the honest bound on everything the register says about a
 * jurisdiction: laws change, and the module's claim is only ever "this is what the source said on
 * this date", never "this is the law today".
 */
export interface RuleProvenance {
  /**
   * The citation the source line shows (CCM-BE11, CCM-BE11-B; D-CCM-34), such as "D.C. Code
   * § 44-1701 et seq.": the governing entry's label, or the label of the rule's freshest live-checked
   * source, which {@link RuleProvenance.ruleSourceDate} then dates. Absent, the line shows no citation
   * text — never the library's research record, which this module doesn't read.
   */
  ruleDisplayCitation?: string;
  ruleSourceUrl?: string;
  ruleSourceCheckedOn: string;
  ruleSourceCheckStatus: string;
  /**
   * The date the source line shows (CCM-BE12, set C Q11): the governing entry's own date, or the
   * latest "unchanged" check of it once CCM-OPS1 records one — and beside a fallback label, that
   * label's own source's date (CCM-BE11-B). The backend resolves which; this module never computes
   * it. Falls back to `ruleSourceCheckedOn` from a backend that predates the field.
   */
  ruleSourceDate: string;
  /**
   * What happened on that date: `checked_live` (the official source was read against its live
   * version that day) or `research_notes` (Givmo's research notes recorded it). ⚠ Anything else —
   * absent, unknown — is `undefined`, and the line then takes the LESS-asserting wording: a source
   * this build cannot vouch was read live is never called checked.
   */
  ruleSourceDateBasis?: 'checked_live' | 'research_notes';
  /**
   * CCM-OPS1-STORE (D-CCM-5): the day a check found a possible change to one of this rule's
   * official sources, while that change is under review; absent otherwise, and absent everywhere
   * while the backend's source checks are switched off. A civil date. The source line keeps its own
   * date and adds Jonathan's Q3 sentence, worded by {@link ruleSourceDateBasis}. It flags nobody's
   * filing.
   */
  ruleSourceChangeFoundOn?: string;
  /**
   * Whether every possible change under review sits on the one source this line names and dates
   * (CCM-BE11-B's fold): `false` when one sits on another of the rule's official sources, so "this
   * source" would name the wrong one. Absent with no change, and from a backend that predates it;
   * the change's sentence is then worded by {@link ruleSourceDateBasis} alone.
   */
  ruleSourceChangeOnLineSource?: boolean;
  /**
   * CCM-OPS1-RECHECK (D-CCM-5 item 4): when the reading organization's OWN filing activity asked for
   * this rule's official sources to be read again, while that re-check is pending. Served on the
   * organization's own obligation reads only (never on a candidate, the picker or another
   * organization's), and never beside an open change. An INSTANT, so it is worded as the viewer's
   * local calendar date ({@link displayLocalDate}).
   */
  ruleSourceRecheckRequestedAt?: string;
}

/**
 * Every field read here is declared on the committed snapshot's `ComplianceRuleProvenanceOut`. The
 * library's research record and its capture note left the reads with CCM-BE11-D (D-CCM-34): an older
 * backend still serves them, and this decode reads neither.
 */
function decodeProvenance(raw: unknown): RuleProvenance {
  const row = isRecord(raw) ? raw : {};
  const basis = row.rule_source_date_basis;
  return {
    // A blank label is no citation: read it as absent.
    ruleDisplayCitation: optionalStr(row.rule_display_citation)?.trim() || undefined,
    ruleSourceUrl: optionalStr(row.rule_source_url),
    ruleSourceCheckedOn: str(row.rule_source_checked_on),
    ruleSourceCheckStatus: str(row.rule_source_check_status),
    ruleSourceDate: optionalStr(row.rule_source_date) ?? str(row.rule_source_checked_on),
    ruleSourceDateBasis: basis === 'checked_live' || basis === 'research_notes' ? basis : undefined,
    ruleSourceChangeFoundOn: optionalStr(row.rule_source_change_found_on),
    // Strict: only an explicit boolean says where the change sits; anything else is no answer.
    ruleSourceChangeOnLineSource:
      row.rule_source_change_on_line_source === true
        ? true
        : row.rule_source_change_on_line_source === false
          ? false
          : undefined,
    ruleSourceRecheckRequestedAt: optionalStr(row.rule_source_recheck_requested_at),
  };
}

/** The provenance decode, for this package's other decoders (`compliance-filings-decode.ts`). */
export function decodeProvenanceOf(raw: unknown): RuleProvenance {
  return decodeProvenance(raw);
}

// ── Families and standing extensions — shared with `compliance-filings-decode.ts` ───────────────

/** The six kinds of filing, a closed enum on the wire. It names a kind, never that anyone must file. */
export type FilingFamily =
  | 'fundraising_registrations'
  | 'corporate_filings'
  | 'tax_filings'
  | 'payroll_filings'
  | 'licenses_and_permits'
  | 'other_filings';

/** The page's fixed order of families (DESIGN-NOTE-1b §2.1). */
export const FILING_FAMILIES: readonly FilingFamily[] = [
  'fundraising_registrations',
  'corporate_filings',
  'tax_filings',
  'payroll_filings',
  'licenses_and_permits',
  'other_filings',
];

/** A served family, or `undefined` for anything this build does not know. */
export function filingFamilyOf(value: unknown): FilingFamily | undefined {
  return (FILING_FAMILIES as readonly unknown[]).includes(value)
    ? (value as FilingFamily)
    : undefined;
}

/**
 * A standing extension of a deadline (CCM-R1-INTAKE-MDEXT): the date it runs to, who it reaches in
 * the source's own words, and the authority it rests on. Served BESIDE the statutory date, never in
 * its place, and only complete: missing any of the three, there is no extension to show — a date
 * without its basis is the bare claim this module never makes.
 */
export interface DeadlineExtension {
  extendedDueOn: string;
  condition: string;
  citation: string;
}

export function decodeDeadlineExtension(raw: unknown): DeadlineExtension | undefined {
  if (!isRecord(raw)) return undefined;
  const extendedDueOn = optionalStr(raw.extended_due_on);
  const condition = optionalStr(raw.condition)?.trim();
  const citation = optionalStr(raw.citation)?.trim();
  if (extendedDueOn === undefined || !condition || !citation) return undefined;
  return { extendedDueOn, condition, citation };
}

/**
 * Where a filing's first year counts from another filing's sending (CCM-R1-MD-ANNUAL-REPORT-FIRST-YEAR;
 * D-CCM-110 J-F: Maryland's first annual report covers the first fiscal year that ends after the
 * registration statement was sent). Served beside a worked-out date and an occurrence, read when it
 * is served: the filing it counts from, the day the register records that filing first sent (absent
 * while it records none, and the year waits), and whether this date's year is the first that sending
 * dates. Only complete: without the filing it counts from there is nothing to say, and a backend that
 * serves none leaves every date as it was.
 */
export interface FirstPeriod {
  jurisdictionCode: string;
  ruleId: string;
  sentOn?: string;
  first: boolean;
}

export function decodeFirstPeriod(raw: unknown): FirstPeriod | undefined {
  if (!isRecord(raw)) return undefined;
  const jurisdictionCode = optionalStr(raw.jurisdiction_code);
  const ruleId = optionalStr(raw.rule_id);
  if (!jurisdictionCode || !ruleId) return undefined;
  const sentOn = optionalStr(raw.sent_on);
  return {
    jurisdictionCode,
    ruleId,
    ...(sentOn !== undefined ? { sentOn } : {}),
    first: raw.first === true,
  };
}

/**
 * Whether an undated year waits on the sending its first year counts from: the register records none
 * yet (D-CCM-110 J-F). Read after a missing fiscal year end, which leaves every year undated for its
 * own reason.
 */
export function waitsOnTheFirstPeriod(row: { dueOn?: string; firstPeriod?: FirstPeriod }): boolean {
  return (
    row.dueOn === undefined && row.firstPeriod !== undefined && row.firstPeriod.sentOn === undefined
  );
}

/** One fact the organization recorded about itself, as the backend renders it for display. */
export interface RecordedParameter {
  parameterKey: string;
  parameterLabel: string;
  recordedValue: string;
  recordedOn: string;
}

function decodeRecordedParameters(raw: unknown): RecordedParameter[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    parameterKey: str(row.parameter_key),
    parameterLabel: str(row.parameter_label),
    recordedValue: str(row.recorded_value),
    recordedOn: str(row.recorded_on),
  }));
}

/** The recorded-parameter decode, for this package's other decoders. */
export function decodeRecordedParametersOf(raw: unknown): RecordedParameter[] {
  return decodeRecordedParameters(raw);
}

// ── Registration imports — the row the §4 composition is read FROM ─────────────────────────────

/**
 * A registration the organization already held when it arrived.
 *
 * ⚠ THIS ROW, NOT `candidate_origin`, IS WHERE THE UNVERIFIED-IMPORT STATE LIVES. See
 * {@link buildFilingRegisterEntries} for the composition and why the distinction is load-bearing.
 */
export interface RegistrationImport {
  importId: string;
  obligationId: string;
  jurisdictionCode: string;
  ruleId: string;
  /** The number the organization typed. A recorded value, never a confirmation of anything. */
  recordedRegistrationNumber?: string;
  registrationDate?: string;
  recordedFiledOn?: string;
  confirmationArtifactId?: string;
  artifactState: string;
  filingRecordState: string;
  importedAt: string;
  /**
   * The latest correction to {@link recordedRegistrationNumber}, or undefined where it has never
   * been amended.
   *
   * ⚠ **ADDITIVE — IT NEVER REPLACES THE ORIGINAL.** `recordedRegistrationNumber` above keeps saying
   * exactly what the organization first typed; this says what it says now instead. A renderer shows
   * the amended value LABELLED as an amendment, with the original still visible: silently swapping
   * one for the other makes a correction indistinguishable from a first recording and loses the fact
   * that the organization changed its answer — which is the whole reason the backend recorded it
   * beside rather than over.
   */
  amendedRegistrationNumber?: string;
  /** How many corrections this import carries. Zero is the ordinary case. */
  amendmentCount: number;
}

function decodeRegistrationImport(row: Record<string, unknown>): RegistrationImport {
  return {
    importId: str(row.import_id),
    obligationId: str(row.obligation_id),
    jurisdictionCode: str(row.jurisdiction_code),
    ruleId: str(row.rule_id),
    recordedRegistrationNumber: optionalStr(row.recorded_registration_number),
    registrationDate: optionalStr(row.registration_date),
    recordedFiledOn: optionalStr(row.recorded_filed_on),
    confirmationArtifactId: optionalStr(row.confirmation_artifact_id),
    artifactState: str(row.artifact_state),
    filingRecordState: str(row.filing_record_state),
    importedAt: str(row.imported_at),
    amendedRegistrationNumber: optionalStr(row.amended_registration_number),
    amendmentCount: int(row.amendment_count),
  };
}

/** One recorded correction, with both sides of it and who made it. */
export interface RegistrationImportAmendment {
  amendmentId: string;
  importId: string;
  obligationId: string;
  fieldKey: string;
  /** What the row said BEFORE this amendment — the original for the first, the prior one after. */
  previousValue?: string;
  amendedValue?: string;
  reason?: string;
  recordedByUserId: string;
  recordedAt: string;
}

export function extractImportAmendments(body: unknown): RegistrationImportAmendment[] {
  return extractList<unknown>(body)
    .filter(isRecord)
    .map((row) => ({
      amendmentId: str(row.amendment_id),
      importId: str(row.import_id),
      obligationId: str(row.obligation_id),
      fieldKey: str(row.field_key),
      previousValue: optionalStr(row.previous_value),
      amendedValue: optionalStr(row.amended_value),
      reason: optionalStr(row.reason),
      recordedByUserId: str(row.recorded_by_user_id),
      recordedAt: str(row.recorded_at),
    }));
}

/**
 * The only field an imported registration may be corrected on.
 *
 * The backend's enum has exactly this one member, and its membership is one of the three independent
 * mechanisms keeping an amendment away from the receipt rule. Adding a value here without the wire
 * having one is a 422, not a feature.
 */
export const AMENDABLE_IMPORT_FIELD = 'recorded_registration_number';

export function extractRegistrationImports(body: unknown): RegistrationImport[] {
  return extractList<unknown>(body).filter(isRecord).map(decodeRegistrationImport);
}

/** The import write's answer: the one row, with the obligation it placed. Undefined without one. */
export function decodeRegistrationImportBody(body: unknown): RegistrationImport | undefined {
  if (!isRecord(body)) return undefined;
  const row = decodeRegistrationImport(body);
  return row.obligationId.length > 0 ? row : undefined;
}

// ── The receipt vault ──────────────────────────────────────────────────────────────────────────

/** The three Word types (CCM-R1-INTAKE-WORD, D-CCM-21 Q8), as the backend normalizes them. */
const WORD_DOC = 'application/msword';
const WORD_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const WORD_DOCM = 'application/vnd.ms-word.document.macroenabled.12';

/**
 * The content types a compliance document may be UPLOADED as — the backend's allow-list, copied.
 *
 * ⚠ **NOT INVENTED HERE, AND NOT WIDENED HERE.** These are exactly
 * `COMPLIANCE_ARTIFACT_UPLOAD_MIMES` in `charity_compliance_artifact_entities.py`: its stored set plus
 * a `.docm`, which the backend stores as the `.docx` it wrote without the macros. The type is checked
 * before a URL is minted AND bound into the signature, so a list that drifted wider than the backend's
 * would produce a file picker offering a type whose upload cannot be signed. Lower case, as the
 * backend normalizes (and signs) it.
 *
 * ⚠ **NO SVG, DELIBERATELY.** An SVG is a script container, and a jurisdiction's confirmation letter
 * is opened by someone who did not choose to run one. Do not add it.
 */
export const ALLOWED_ARTIFACT_CONTENT_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/tiff',
  WORD_DOC,
  WORD_DOCX,
  WORD_DOCM,
];

/**
 * A Word file typed by its extension, for a browser that reports no type for it (a machine without
 * Office often reports `""` or a generic type for `.docx`, `.doc` and `.docm`).
 */
const WORD_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.doc': WORD_DOC,
  '.docx': WORD_DOCX,
  '.docm': WORD_DOCM,
};

/** Types a browser reports when it does not know a file's own type. */
const UNTYPED_BROWSER_TYPES: readonly string[] = [
  '',
  'application/octet-stream',
  'application/zip',
  'application/x-zip-compressed',
];

/**
 * The file picker's `accept`, derived from the allow-list rather than spelled a second time, plus the
 * Word extensions so a picker whose platform does not know the Word types still offers the files.
 */
export const ARTIFACT_FILE_ACCEPT = [
  ...ALLOWED_ARTIFACT_CONTENT_TYPES,
  ...Object.keys(WORD_TYPE_BY_EXTENSION),
].join(',');

/** Whether a file type is one the backend will sign an upload for (case-insensitive). */
export function isAllowedArtifactContentType(contentType: string): boolean {
  return ALLOWED_ARTIFACT_CONTENT_TYPES.includes(contentType.trim().toLowerCase());
}

/**
 * The content type to upload a picked file as, or `undefined` when the vault does not take it: the
 * browser's own type when it is allow-listed, and a Word file's type by its extension when the
 * browser reported none (or a generic one). Never a type the backend would refuse.
 */
export function artifactContentTypeFor(file: { name: string; type: string }): string | undefined {
  const reported = file.type.trim().toLowerCase();
  if (ALLOWED_ARTIFACT_CONTENT_TYPES.includes(reported)) return reported;
  if (!UNTYPED_BROWSER_TYPES.includes(reported)) return undefined;
  const dot = file.name.lastIndexOf('.');
  const extension = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
  return WORD_TYPE_BY_EXTENSION[extension];
}

/**
 * Read the attachment stamp, FAIL-SAFE.
 *
 * ⚠ The direction here is the opposite of {@link bool}, and deliberately so. This flag is the only
 * thing standing between a cited receipt and a delete control, so an absent, malformed or
 * unrecognized value must read as ATTACHED — the restrictive answer. Only an explicit `false` means
 * "nothing points at this yet". The backend states its own predicate positively for the same reason:
 * an unreadable value must never resolve towards "safe to remove".
 */
function attachedFlag(value: unknown): boolean {
  return value !== false;
}

/** One document in the organization's vault. Never the storage key, never a read URL. */
export interface ComplianceArtifact {
  artifactId: string;
  orgRef: string;
  fileName: string;
  contentType: string;
  byteSize?: number;
  /** Self-reported by the uploader; the platform does not compute or check it. */
  reportedContentDigest?: string;
  uploadedByUserId?: string;
  uploadedAt?: string;
  /** When a filing transition or recorded import first named this document. Never clears. */
  attachedAt?: string;
  /** True once anything in the register points at this document — see {@link attachedFlag}. */
  isAttached: boolean;
  /**
   * CCM-R1-INTAKE-WORD. True when the upload carried macros and the stored file is the Word file Givmo
   * wrote from it without them. Only an explicit `true` counts: the notice it drives makes a claim.
   */
  macrosRemoved: boolean;
}

function decodeArtifact(row: Record<string, unknown>): ComplianceArtifact {
  const attachedAt = optionalStr(row.attached_at);
  return {
    artifactId: str(row.artifact_id),
    orgRef: str(row.org_ref),
    fileName: str(row.file_name),
    contentType: str(row.content_type),
    byteSize:
      typeof row.byte_size === 'number' && Number.isFinite(row.byte_size)
        ? row.byte_size
        : undefined,
    reportedContentDigest: optionalStr(row.reported_content_digest),
    uploadedByUserId: optionalStr(row.uploaded_by_user_id),
    uploadedAt: optionalStr(row.uploaded_at),
    attachedAt,
    // Either signal alone is enough to freeze it. A stamp with a missing flag, or a flag with a
    // missing stamp, both mean something referenced this document.
    isAttached: attachedFlag(row.is_attached) || attachedAt !== undefined,
    macrosRemoved: row.macros_removed === true,
  };
}

export function extractArtifacts(body: unknown): ComplianceArtifact[] {
  return extractList<unknown>(body).filter(isRecord).map(decodeArtifact);
}

/** Decode the single artifact the confirm step returns. */
export function decodeConfirmedArtifact(body: unknown): ComplianceArtifact | undefined {
  return isRecord(body) ? decodeArtifact(body) : undefined;
}

/**
 * Whether the organization may remove this document.
 *
 * ⚠ **THE WIRE ANSWERS THIS; THIS FUNCTION DOES NOT INFER IT.** `is_attached` is projected onto the
 * artifact DTO by the backend precisely so a client renders the refusal it would get from the delete
 * route without reconstructing it from a timestamp. A cited document gets NO delete control —
 * absent, not disabled — because the refusal is permanent rather than a state that might pass.
 */
export function isArtifactDeletable(artifact: ComplianceArtifact): boolean {
  return !artifact.isAttached;
}

/** WHY an obligation names a document. Derived by the backend from WHERE the reference was found. */
export type ArtifactReferenceRole = 'confirmation' | 'submission_proof';

/**
 * One document AS ONE OBLIGATION NAMES IT.
 *
 * ⚠ `submissionProof` says the ORGANIZATION sent something and never that a jurisdiction answered.
 * No predicate reads it and no copy may present it as a receipt.
 */
export interface ObligationArtifact {
  artifactId: string;
  obligationId: string;
  referenceRole: string;
  /** True when the obligation still carries this reference; false when only the event log holds it. */
  isCurrent: boolean;
  fileName: string;
  contentType: string;
  byteSize?: number;
  uploadedAt?: string;
  attachedAt?: string;
}

export function extractObligationArtifacts(body: unknown): ObligationArtifact[] {
  return extractList<unknown>(body)
    .filter(isRecord)
    .map((row) => ({
      artifactId: str(row.artifact_id),
      obligationId: str(row.obligation_id),
      referenceRole: str(row.reference_role),
      // ⚠ Strict: an unreadable value renders as HISTORICAL rather than current. A reference the
      // obligation no longer carries shown as current would make a dismissed filing look like one
      // that still stands.
      isCurrent: row.is_current === true,
      fileName: str(row.file_name),
      contentType: str(row.content_type),
      byteSize:
        typeof row.byte_size === 'number' && Number.isFinite(row.byte_size)
          ? row.byte_size
          : undefined,
      uploadedAt: optionalStr(row.uploaded_at),
      attachedAt: optionalStr(row.attached_at),
    }));
}

/** The signed PUT grant. `objectRef` is the one storage key that ever reaches the wire. */
export interface ArtifactUploadGrant {
  uploadUrl: string;
  objectRef: string;
  /** ⚠ The type the signature was built over — send exactly this, not the browser's guess. */
  contentType: string;
  /**
   * CCM-R1-UPLOAD-SIZE-CAP: every header the signature binds, each with the exact value to send (the
   * type, the disposition and the size range), and the largest file the link takes. A backend that
   * serves neither signs the type and the disposition alone.
   */
  headers?: Record<string, string>;
  maxBytes?: number;
}

export function decodeUploadGrant(body: unknown): ArtifactUploadGrant | undefined {
  if (!isRecord(body)) return undefined;
  const uploadUrl = str(body.upload_url);
  const objectRef = str(body.object_ref);
  const contentType = str(body.content_type);
  // A grant missing any of the three cannot be acted on, and guessing a value would produce a PUT
  // whose signature cannot match. Undefined so the caller renders its own failure.
  if (uploadUrl.length === 0 || objectRef.length === 0 || contentType.length === 0)
    return undefined;
  // Every header or none: a header dropped from a signed set is a PUT storage refuses.
  const headers =
    isRecord(body.headers) &&
    Object.values(body.headers).every((value) => typeof value === 'string')
      ? (body.headers as Record<string, string>)
      : undefined;
  const maxBytes =
    typeof body.max_bytes === 'number' && Number.isInteger(body.max_bytes) && body.max_bytes > 0
      ? body.max_bytes
      : undefined;
  return {
    uploadUrl,
    objectRef,
    contentType,
    ...(headers !== undefined && Object.keys(headers).length > 0 ? { headers } : {}),
    ...(maxBytes !== undefined ? { maxBytes } : {}),
  };
}

/** A short-lived read link, minted per request and never persisted. */
export interface ArtifactDownload {
  artifactId: string;
  fileName: string;
  contentType: string;
  downloadUrl: string;
}

export function decodeArtifactDownload(body: unknown): ArtifactDownload | undefined {
  if (!isRecord(body)) return undefined;
  const downloadUrl = str(body.download_url);
  if (downloadUrl.length === 0) return undefined;
  return {
    artifactId: str(body.artifact_id),
    fileName: str(body.file_name),
    contentType: str(body.content_type),
    downloadUrl,
  };
}

/**
 * A stored document's size, for display.
 *
 * A count of bytes the platform OBSERVED, not a figure anybody asserted — so unlike money it is
 * arithmetic-safe. Absent renders as nothing rather than as zero: "0 bytes" would state a size for a
 * document whose size we could not read.
 */
export function displayArtifactSize(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Obligations ────────────────────────────────────────────────────────────────────────────────

/** CCM-R1-LATE-FILINGS: a late fee as the rule's own source states it, and where it says so. */
export interface LateFee {
  quotedSourceText: string;
  citation: string;
}

/** A late fee with both its words and its citation, or nothing: half a citation is never shown. */
function decodeLateFee(raw: unknown): LateFee | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const fee = raw as Record<string, unknown>;
  const quotedSourceText = optionalStr(fee.quoted_source_text);
  const citation = optionalStr(fee.citation);
  return quotedSourceText !== undefined && citation !== undefined
    ? { quotedSourceText, citation }
    : undefined;
}

/** One obligation in the organization's register. */
export interface ComplianceObligation {
  obligationId: string;
  jurisdictionCode: string;
  jurisdictionName: string;
  ruleId: string;
  ruleVersionId: string;
  obligationClass: string;
  candidateState: string;
  candidateOrigin: string;
  filingRecordState: string;
  confirmationArtifactId?: string;
  /** The confirmation number the STATE issued, where the organization recorded one. */
  confirmationReference?: string;
  recordedFiledOn?: string;
  submissionRecordedOn?: string;
  refilingWindowClosedOn?: string;
  /**
   * CCM-R1-WINDOW-LABEL-B: served only on a recorded filing dated after the window it was recorded
   * from had closed — that window's close date, counting an automatic extension. The backend
   * decides it once (`filed_after_window_closed_on`); nothing here compares the dates.
   */
  filedAfterWindowClosedOn?: string;
  /**
   * CCM-R1-WINDOW-LABEL-D: served only beside `filedAfterWindowClosedOn` — the year (its cycle's id)
   * whose window that filing came after, so the row's act lands on that year's tile. The backend
   * decides it; nothing here compares the dates. Absent where no current year closed on that day.
   */
  filedAfterWindowClosedCycleId?: string;
  /** Set when the rule this obligation was confirmed against has since been re-versioned. */
  ruleChangeFlaggedOn?: string;
  /**
   * CCM-R1-REVIEW-CHANGES (cut 1008-11's D7): set while the organization has stopped tracking this
   * filing. It stays confirmed with its record and history, and Givmo shows no new dates for it.
   * Absent from a backend before be#2770.
   */
  trackingStoppedAt?: string;
  trackingStoppedByUserId?: string;
  /**
   * CCM-R1-AI-SETTING-VIA-AI: how tracking was stopped, `web` (a person in Givmo) or `connector`
   * (through their connected AI). Served only while it is stopped; absent from a backend that
   * doesn't say, and on a stop from before it was kept.
   */
  trackingStoppedChannel?: string;
  /**
   * CCM-MOD5-RECORD-FILING-LINK (D-CCM-97): the organization's record this library filing is, where
   * it linked one. Absent for every other filing, and from a backend before cut 1008-76.
   */
  vendorLink?: ObligationVendorLink;
  registrationDate?: string;
  /**
   * CCM-R1-INTAKE-ANCHOR: the expiry printed on the licence, which a licence-period renewal and the
   * report due after the licence period run from. Absent until recorded.
   */
  licenseExpiresOn?: string;
  /** The organization's own proof of sending. Never a confirmation, and nothing reads it. */
  submissionProofArtifactId?: string;
  /**
   * CCM-R1-LATE-FILINGS (D-CCM-62, L4): a late fee in the rule's own source's words, with its
   * citation, served only where the source states one. Never an amount: nothing here works one out.
   */
  lateFee?: LateFee;
  /** CCM-R1-FILING-CYCLES: a ONE-TIME filing's copy as sent ("Filing as sent"). Never a receipt. */
  filedCopyArtifactId?: string;
  ruleSummary: string;
  /** CCM-R1-FILING-META: the Filings page's family for the rule. Unknown is absent. */
  filingFamily?: FilingFamily;
  /**
   * Whether the wire served a family at all. `false` means the rule is no filing (the presentation
   * serves `null` for one), and the Filings page lists it nowhere; `true` with `filingFamily`
   * absent is a family this build does not know, listed rather than dropped.
   */
  filingFamilyServed: boolean;
  /** CCM-R1-FILING-META: the rule's short title ("Annual report"). */
  displayTitle?: string;
  /** CCM-R1-FILING-META: what the organization's own number is called ("License number"). */
  numberLabel?: string;
  provenance: RuleProvenance;
  recordedParameters: RecordedParameter[];
  parameterSource: string;
  note?: string;
  dismissalReason?: string;
  confirmedAt?: string;
  dismissedAt?: string;
}

/**
 * The record a library filing is linked to (`vendor_link`), with the record's facts the filing needs
 * read when served, never stored twice: its date fills a filing Givmo has no date for (D-CCM-97 (2)).
 */
export interface ObligationVendorLink {
  linkId: string;
  vendorId: string;
  recordKind: string;
  jurisdictionCode: string;
  /** The record's renewal date: the organization's own, which row 7 exempts. */
  renewalOn?: string;
  isCurrent: boolean;
  linkedByUserId?: string;
  linkedAt?: string;
  /** `web` (a person in Givmo) or `connector` (that person's connected AI). */
  linkedChannel?: string;
}

function decodeObligationVendorLink(raw: unknown): ObligationVendorLink | undefined {
  if (!isRecord(raw)) return undefined;
  const linkId = optionalStr(raw.link_id);
  const vendorId = optionalStr(raw.vendor_id);
  if (linkId === undefined || vendorId === undefined) return undefined;
  return {
    linkId,
    vendorId,
    recordKind: str(raw.record_kind),
    jurisdictionCode: str(raw.jurisdiction_code),
    renewalOn: optionalStr(raw.renewal_on),
    isCurrent: raw.is_current === true,
    linkedByUserId: optionalStr(raw.linked_by_user_id),
    linkedAt: optionalStr(raw.linked_at),
    linkedChannel: optionalStr(raw.linked_channel),
  };
}

function decodeObligation(row: Record<string, unknown>): ComplianceObligation {
  return {
    obligationId: str(row.obligation_id),
    jurisdictionCode: str(row.jurisdiction_code),
    jurisdictionName: str(row.jurisdiction_name),
    ruleId: str(row.rule_id),
    ruleVersionId: str(row.rule_version_id),
    obligationClass: str(row.obligation_class),
    candidateState: str(row.candidate_state),
    candidateOrigin: str(row.candidate_origin),
    filingRecordState: str(row.filing_record_state),
    confirmationArtifactId: optionalStr(row.confirmation_artifact_id),
    confirmationReference: optionalStr(row.confirmation_reference),
    recordedFiledOn: optionalStr(row.recorded_filed_on),
    submissionRecordedOn: optionalStr(row.submission_recorded_on),
    refilingWindowClosedOn: optionalStr(row.refiling_window_closed_on),
    filedAfterWindowClosedOn: optionalStr(row.filed_after_window_closed_on),
    filedAfterWindowClosedCycleId: optionalStr(row.filed_after_window_closed_cycle_id),
    ruleChangeFlaggedOn: optionalStr(row.rule_change_flagged_on),
    trackingStoppedAt: optionalStr(row.tracking_stopped_at),
    trackingStoppedByUserId: optionalStr(row.tracking_stopped_by_user_id),
    trackingStoppedChannel: optionalStr(row.tracking_stopped_channel),
    vendorLink: decodeObligationVendorLink(row.vendor_link),
    registrationDate: optionalStr(row.registration_date),
    licenseExpiresOn: optionalStr(row.license_expires_on),
    submissionProofArtifactId: optionalStr(row.submission_proof_artifact_id),
    lateFee: decodeLateFee(row.late_fee),
    filedCopyArtifactId: optionalStr(row.filed_copy_artifact_id),
    ruleSummary: str(row.rule_summary),
    filingFamily: filingFamilyOf(row.filing_family),
    filingFamilyServed: typeof row.filing_family === 'string' && row.filing_family.length > 0,
    displayTitle: optionalStr(row.display_title),
    numberLabel: optionalStr(row.number_label),
    provenance: decodeProvenance(row.provenance),
    recordedParameters: decodeRecordedParameters(row.recorded_parameters),
    parameterSource: str(row.parameter_source),
    note: optionalStr(row.note),
    dismissalReason: optionalStr(row.dismissal_reason),
    confirmedAt: optionalStr(row.confirmed_at),
    dismissedAt: optionalStr(row.dismissed_at),
  };
}

export function extractObligations(body: unknown): ComplianceObligation[] {
  return extractList<unknown>(body).filter(isRecord).map(decodeObligation);
}

// ── Occurrences — the computed cycles ──────────────────────────────────────────────────────────

/** One alert lead on one cycle. Data the backend computed, not a notification. */
export interface OccurrenceAlertLead {
  leadDays: number;
  alertOn: string;
  leadReason: string;
}

/** One dated cycle of one obligation's deadline. */
export interface ComplianceOccurrence {
  occurrenceId: string;
  obligationId: string;
  jurisdictionCode: string;
  jurisdictionName: string;
  ruleId: string;
  obligationClass: string;
  cycleKey: string;
  deadlineIndex: number;
  /** WHICH deadline shape produced (or failed to produce) this date. See {@link describeCycleTiming}. */
  dueDateBasis: string;
  dueOn?: string;
  /** CCM-R1-INTAKE-MDEXT: a standing extension, beside `dueOn` and never in its place. */
  extension?: DeadlineExtension;
  uncomputableReason?: string;
  /** The organization's own recorded year end for a cycle the jurisdiction publishes no date for.
   *  ⚠ NOT a due date — see {@link describeCycleTiming}. */
  cadencePromptOn?: string;
  filingRecordState: string;
  confirmationArtifactId?: string;
  recordedFiledOn?: string;
  submissionRecordedOn?: string;
  ruleSummary: string;
  /** CCM-R1-FILING-META, as on the obligation. */
  filingFamily?: FilingFamily;
  displayTitle?: string;
  numberLabel?: string;
  provenance: RuleProvenance;
  computedFrom: RecordedParameter[];
  parameterSource: string;
  alertLeads: OccurrenceAlertLead[];
  /** CCM-R1-MD-ANNUAL-REPORT-FIRST-YEAR: where this year's filing counts its first year from. */
  firstPeriod?: FirstPeriod;
}

function decodeAlertLeads(raw: unknown): OccurrenceAlertLead[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecord).map((row) => ({
    leadDays: int(row.lead_days),
    alertOn: str(row.alert_on),
    leadReason: str(row.lead_reason),
  }));
}

function decodeOccurrence(row: Record<string, unknown>): ComplianceOccurrence {
  return {
    occurrenceId: str(row.occurrence_id),
    obligationId: str(row.obligation_id),
    jurisdictionCode: str(row.jurisdiction_code),
    jurisdictionName: str(row.jurisdiction_name),
    ruleId: str(row.rule_id),
    obligationClass: str(row.obligation_class),
    cycleKey: str(row.cycle_key),
    deadlineIndex: int(row.deadline_index),
    dueDateBasis: str(row.due_date_basis),
    dueOn: optionalStr(row.due_on),
    extension: decodeDeadlineExtension(row.extension),
    uncomputableReason: optionalStr(row.uncomputable_reason),
    cadencePromptOn: optionalStr(row.cadence_prompt_on),
    filingRecordState: str(row.filing_record_state),
    confirmationArtifactId: optionalStr(row.confirmation_artifact_id),
    recordedFiledOn: optionalStr(row.recorded_filed_on),
    submissionRecordedOn: optionalStr(row.submission_recorded_on),
    ruleSummary: str(row.rule_summary),
    filingFamily: filingFamilyOf(row.filing_family),
    displayTitle: optionalStr(row.display_title),
    numberLabel: optionalStr(row.number_label),
    provenance: decodeProvenance(row.provenance),
    computedFrom: decodeRecordedParameters(row.computed_from),
    parameterSource: str(row.parameter_source),
    alertLeads: decodeAlertLeads(row.alert_leads),
    firstPeriod: decodeFirstPeriod(row.first_period),
  };
}

export function extractOccurrences(body: unknown): ComplianceOccurrence[] {
  return extractList<unknown>(body).filter(isRecord).map(decodeOccurrence);
}

/** One entry on an obligation's append-only log. */
export interface ObligationEvent {
  eventId: string;
  eventType: string;
  fromCandidateState?: string;
  toCandidateState?: string;
  fromFilingRecordState?: string;
  toFilingRecordState?: string;
  reason?: string;
  /** Who acted, where the log names a person (an act the system took names none). */
  actorUserId?: string;
  /**
   * The act's own facts as the log recorded them: for a changed date, its `from_…` / `to_…` pair,
   * which the filing panel's "Corrections and changes" reads (CCM-R1-INTAKE-HISTORY). Opaque here,
   * read only where a key is understood; a missing or malformed detail is an empty one.
   */
  detail: Record<string, unknown>;
  occurredAt: string;
}

function decodeEvent(row: Record<string, unknown>): ObligationEvent {
  return {
    eventId: str(row.event_id),
    eventType: str(row.event_type),
    fromCandidateState: optionalStr(row.from_candidate_state),
    toCandidateState: optionalStr(row.to_candidate_state),
    fromFilingRecordState: optionalStr(row.from_filing_record_state),
    toFilingRecordState: optionalStr(row.to_filing_record_state),
    reason: optionalStr(row.reason),
    actorUserId: optionalStr(row.actor_user_id),
    detail: isRecord(row.detail) ? { ...row.detail } : {},
    occurredAt: str(row.occurred_at),
  };
}

/** The obligation detail read: the row, its computed cycles, and its event log. */
export interface ObligationDetail {
  obligation?: ComplianceObligation;
  occurrences: ComplianceOccurrence[];
  events: ObligationEvent[];
}

export function decodeObligationDetail(body: unknown): ObligationDetail {
  const row = isRecord(body) ? body : {};
  return {
    obligation: isRecord(row.obligation) ? decodeObligation(row.obligation) : undefined,
    occurrences: Array.isArray(row.occurrences)
      ? row.occurrences.filter(isRecord).map(decodeOccurrence)
      : [],
    events: Array.isArray(row.events) ? row.events.filter(isRecord).map(decodeEvent) : [],
  };
}

// ── The recorded profile ───────────────────────────────────────────────────────────────────────

/** One recorded parameter row, with its attribution and its place in the supersession chain. */
export interface ProfileParameter {
  parameterId: string;
  parameterKey: string;
  parameterLabel: string;
  valueKind: string;
  recordedValue: string;
  parameterSource: string;
  sourceNote?: string;
  status: string;
  /** WHO recorded it. Attribution is carried on every row; the backend assigns it, never this app. */
  recordedByUserId: string;
  recordedAt: string;
  supersededAt?: string;
  supersededByParameterId?: string;
  /**
   * CCM-R1-STATES-NOT-SOLICITED (D-CCM-72): the states list stated complete, so every state not on
   * it is one where the organization doesn't ask for contributions. Only ever on that list's row.
   */
  listComplete?: true;
  /** CCM-BE24-B (D-CCM-74): the answer a moved fiscal year end was recorded with. */
  yearEndChange?: YearEndChange;
}

/** CCM-BE24-B: whether the earlier fiscal year end was wrong, or the fiscal year changed. */
export type YearEndChange = 'correction' | 'change';

function decodeProfileParameter(row: Record<string, unknown>): ProfileParameter {
  return {
    parameterId: str(row.parameter_id),
    parameterKey: str(row.parameter_key),
    parameterLabel: str(row.parameter_label),
    valueKind: str(row.value_kind),
    recordedValue: str(row.recorded_value),
    parameterSource: str(row.parameter_source),
    sourceNote: optionalStr(row.source_note),
    status: str(row.status),
    recordedByUserId: str(row.recorded_by_user_id),
    recordedAt: str(row.recorded_at),
    supersededAt: optionalStr(row.superseded_at),
    supersededByParameterId: optionalStr(row.superseded_by_parameter_id),
    ...(row.jurisdiction_list_complete === true ? { listComplete: true as const } : {}),
    ...(row.year_end_change === 'correction' || row.year_end_change === 'change'
      ? { yearEndChange: row.year_end_change }
      : {}),
  };
}

export function extractProfileParameters(body: unknown): ProfileParameter[] {
  return extractList<unknown>(body).filter(isRecord).map(decodeProfileParameter);
}

/** The organization's compliance profile as it stands. */
export interface ComplianceProfile {
  orgRef: string;
  parameters: ProfileParameter[];
  /**
   * WHICH required facts are absent. This is why a register can legitimately be empty, and telling
   * the organization which fact is missing is the difference between a product that refuses and one
   * that refuses opaquely.
   */
  missingRequiredParameterKeys: string[];
  parameterFingerprint?: string;
  openReviewFlagCount: number;
  pendingSuggestionCount: number;
  recordedOn?: string;
  /**
   * Whether the organization's Compliance reminders reach anyone (D-CCM-18), from the same read the
   * reminders are sent by. `null` is the backend saying it could not make that read (or a build that
   * does not serve the field): it claims nothing either way, so only a served `false` may say that
   * nobody is reminded.
   */
  reminderRecipientsPresent: boolean | null;
  /**
   * CCM-BE24-B (D-CCM-74): a confirmed filing's years count from the fiscal year end, so moving it
   * asks whether the earlier date was wrong (Y1), where the backend would otherwise refuse the move.
   * Absent where the backend predates it, and with it the states statement (D-CCM-72) that shipped
   * beside it: neither is then offered or sent, since that backend refuses both keys.
   */
  yearEndChangeRequired?: boolean;
}

/** Decode the profile read. */
export function decodeProfile(body: unknown): ComplianceProfile | undefined {
  if (!isRecord(body)) return undefined;
  return {
    orgRef: str(body.org_ref),
    parameters: Array.isArray(body.parameters)
      ? body.parameters.filter(isRecord).map(decodeProfileParameter)
      : [],
    missingRequiredParameterKeys: stringList(body.missing_required_parameter_keys),
    parameterFingerprint: optionalStr(body.parameter_fingerprint),
    openReviewFlagCount: int(body.open_review_flag_count),
    pendingSuggestionCount: int(body.pending_suggestion_count),
    recordedOn: optionalStr(body.recorded_on),
    reminderRecipientsPresent: nullableBool(body.reminder_recipients_present),
    ...(typeof body.year_end_change_required === 'boolean'
      ? { yearEndChangeRequired: body.year_end_change_required }
      : {}),
  };
}

/** A value a machine read off a document the organization uploaded, offered back for confirm-or-correct. */
export interface ProfileSuggestion {
  suggestionId: string;
  parameterKey: string;
  parameterLabel: string;
  suggestedValue: string;
  suggestionSource: string;
  sourceNote?: string;
  state: string;
}

export function extractProfileSuggestions(body: unknown): ProfileSuggestion[] {
  return extractList<unknown>(body)
    .filter(isRecord)
    .map((row) => ({
      suggestionId: str(row.suggestion_id),
      parameterKey: str(row.parameter_key),
      parameterLabel: str(row.parameter_label),
      suggestedValue: str(row.suggested_value),
      suggestionSource: str(row.suggestion_source),
      sourceNote: optionalStr(row.source_note),
      state: str(row.state),
    }));
}

/** One place a figure the organization recorded and a figure Givmo measured disagree. */
export interface ProfileReviewFlag {
  reviewFlagId: string;
  parameterKey: string;
  parameterLabel: string;
  flagKind: string;
  recordedValue?: string;
  observedValue?: string;
  observedSource: string;
  observedAsOf?: string;
  state: string;
  resolution?: string;
}

export function extractProfileReviewFlags(body: unknown): ProfileReviewFlag[] {
  return extractList<unknown>(body)
    .filter(isRecord)
    .map((row) => ({
      reviewFlagId: str(row.review_flag_id),
      parameterKey: str(row.parameter_key),
      parameterLabel: str(row.parameter_label),
      flagKind: str(row.flag_kind),
      recordedValue: optionalStr(row.recorded_value),
      observedValue: optionalStr(row.observed_value),
      observedSource: str(row.observed_source),
      observedAsOf: optionalStr(row.observed_as_of),
      state: str(row.state),
      resolution: optionalStr(row.resolution),
    }));
}

/** What recording one parameter did. */
export interface ParameterRecordResult {
  parameterFingerprintChanged: boolean;
  /** Confirmed obligations whose computed dates were re-anchored against the new facts. */
  obligationsRecomputed: number;
}

export function decodeParameterRecordResult(body: unknown): ParameterRecordResult | undefined {
  if (!isRecord(body)) return undefined;
  return {
    parameterFingerprintChanged: bool(body.parameter_fingerprint_changed),
    obligationsRecomputed: int(body.obligations_recomputed),
  };
}

// ── The 51 jurisdictions the register spans ────────────────────────────────────────────────────

/**
 * The 51 US jurisdictions (50 states + the District of Columbia), matching the backend's
 * `catalog/charity_compliance_rules/*.json` corpus one-for-one.
 *
 * This is a geography list, not legal content: it carries no rule, no citation and no claim about
 * any jurisdiction. It exists so the register can show a row for a jurisdiction the organization has
 * recorded NOTHING in — "nothing recorded here" is a true and useful answer, and a table that
 * silently omitted 40 jurisdictions would let absence read as adequacy.
 *
 * ⚠ Names are used ONLY as a fallback for a jurisdiction with no obligation row. Wherever the wire
 * supplies `jurisdiction_name`, the wire's own name wins — this list never overrides it.
 *
 * ⚠ Not every jurisdiction is derivable: 10 of the 51 (DE, IA, ID, IN, MT, NE, SD, UT, VT, WY)
 * produce no candidates at all, and that is a fact about the corpus rather than about any
 * organization. A row with nothing recorded says exactly that and nothing more.
 */
export const US_JURISDICTIONS: readonly { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// ── The filing-register composition ────────────────────────────────────────────────────────────

/**
 * How one register row reads. Every value names something RECORDED; none names a legal standing.
 *
 * `unrecognized` is not a defensive afterthought — it is what a `filing_record_state` this build does
 * not know about must render as, so a value added to the backend vocabulary appears as something the
 * console cannot yet describe rather than being quietly folded into `nothing_recorded`.
 */
export type FilingRegisterRecordKind =
  | 'nothing_recorded'
  | 'no_filing_recorded'
  | 'imported_awaiting_document'
  | 'registration_filing_recorded'
  | 'exemption_filing_recorded'
  | 'renewal_filing_recorded'
  | 'submission_recorded'
  | 'refiling_window_passed'
  | 'declined'
  | 'unrecognized';

/** One row of the per-jurisdiction filing register. */
export interface FilingRegisterEntry {
  jurisdictionCode: string;
  jurisdictionName: string;
  recordKind: FilingRegisterRecordKind;
  /** The record-shaped label. Never a status claim. */
  recordLabel: string;
  obligation?: ComplianceObligation;
  /** The import row backing this entry, where one exists. The ONLY source of the typed number. */
  registrationImport?: RegistrationImport;
  /** The number the ORGANIZATION typed on import. Present only on an `imported_awaiting_document` row. */
  recordedRegistrationNumber?: string;
  /** Whether a confirmation artifact is on file for this entry. */
  hasReceiptOnFile: boolean;
  /**
   * The document this entry's filing record rests on, where it has one.
   *
   * ⚠ Carried so the register can OPEN the confirmation rather than merely assert it exists. Present
   * only alongside `hasReceiptOnFile` — the two are read from the same pair of fields, so a row can
   * never offer a link it cannot resolve.
   */
  confirmationArtifactId?: string;
  /** The state-issued confirmation number, where the organization recorded one. */
  confirmationReference?: string;
  recordedFiledOn?: string;
  /** The next computed cycle date for this jurisdiction, where one is computable. */
  nextDueOn?: string;
  /** True when the rule this obligation was confirmed against has been re-versioned since. */
  ruleChangeFlagged: boolean;
}

/**
 * The record-shaped labels. Each names an ACT and its record — never a legal conclusion. A closed
 * window's label states its own date, and so does a filing recorded after its window closed, so
 * {@link recordLabel} composes both.
 */
const RECORD_LABELS: Record<Exclude<FilingRegisterRecordKind, 'refiling_window_passed'>, string> = {
  nothing_recorded: 'Nothing recorded',
  no_filing_recorded: 'In register — no filing recorded',
  // The §4 composition. "active (unverified — artifact requested)" is NOT a filing-record state and
  // could never become one; this label describes the arrangement that IS that state.
  imported_awaiting_document: 'In register — recorded on arrival, confirmation document requested',
  registration_filing_recorded: 'Registration filing recorded',
  exemption_filing_recorded: 'Exemption filing recorded',
  renewal_filing_recorded: 'Renewal filing recorded',
  submission_recorded: 'Submission recorded — awaiting confirmation',
  declined: 'Declined',
  unrecognized: 'Recorded state not recognized by this version',
};

/** Whether an entry's filing record is backed by a confirmation the jurisdiction issued. */
export function isReceiptBacked(filingRecordState: string): boolean {
  return (RECEIPT_BACKED_FILING_STATES as readonly string[]).includes(filingRecordState);
}

/** Whether a value is one this build's vocabulary knows. */
export function isKnownFilingRecordState(value: string): value is FilingRecordState {
  return FILING_RECORD_STATES.includes(value);
}

/**
 * Compose ONE obligation (plus the import row that may back it) into a register record kind.
 *
 * ══ THE COMPOSITION IS THE WHOLE POINT ═════════════════════════════════════════════════════════
 *
 * A register row's meaning is `candidate_state` × `filing_record_state` × the import row's
 * `artifact_state`. No single field carries it, and the one arrangement that most invites a shortcut
 * is the one the backend went furthest to make impossible:
 *
 * ⚠ **"Recorded on arrival, confirmation document requested" is read from the IMPORT ROW, never from
 * `candidate_origin`.** `candidate_origin === 'organization_recorded_import'` says only WHERE the
 * candidate came from; it says nothing about whether the document arrived with it. The real state is
 * an obligation with `candidate_state = confirmed` and `filing_record_state = none`, carrying an
 * import row whose `artifact_state` is `requested`. An import that arrived WITH its confirmation
 * went through the ordinary receipt-backed transition and is indistinguishable from any other
 * filing record — as it should be.
 *
 * ⚠ **The typed registration number renders from the import row and is never a confirmation.** It is
 * a number the organization typed. Presenting it beside a receipt-backed filing would let it read as
 * evidence a state issued something, which is exactly what the receipt rule exists to prevent.
 */
export function composeRecordKind(
  obligation: ComplianceObligation | undefined,
  registrationImport: RegistrationImport | undefined,
): FilingRegisterRecordKind {
  if (obligation === undefined) return 'nothing_recorded';

  switch (obligation.candidateState) {
    case 'dismissed':
      return 'declined';
    // A `proposed` candidate is inert: nothing downstream exists until the organization confirms,
    // and a DB CHECK stops it holding any filing record. It is no decision, and
    // `isRegisterMaterial` keeps it from ever LEADING a register row.
    case 'proposed':
      return 'nothing_recorded';
    case 'confirmed':
      break;
    // A candidate state this build does not know. It must NOT collapse to `nothing_recorded` —
    // that would render an obligation the organization has acted on as one it has not touched,
    // which is a false record statement in the most misleading direction.
    default:
      return 'unrecognized';
  }

  switch (obligation.filingRecordState) {
    case 'registered_filed':
      return 'registration_filing_recorded';
    case 'exempt_filed':
      return 'exemption_filing_recorded';
    case 'renewed_filed':
      return 'renewal_filing_recorded';
    case 'submission_recorded':
      return 'submission_recorded';
    case 'window_closed':
      return 'refiling_window_passed';
    case 'none':
      // THE §4 COMPOSITION — read off the import row's artifact state, never off candidate_origin.
      return registrationImport?.artifactState === 'requested'
        ? 'imported_awaiting_document'
        : 'no_filing_recorded';
    default:
      return 'unrecognized';
  }
}

/**
 * The dates a record label states (CCM-R1-WINDOW-LABEL and `-B`): the day a closed window closed
 * with nothing recorded for it; and, for a filing recorded after its window closed, the filing's own
 * date beside the close it came after.
 */
export interface RecordLabelDates {
  /** The day the window closed with nothing recorded for it (`refiling_window_closed_on`). */
  windowClosedOn?: string;
  /** The filing's own date (`recorded_filed_on`), stated only beside the close it came after. */
  filedOn?: string;
  /**
   * The close of the window a recorded filing came after, counting an automatic extension
   * (`filed_after_window_closed_on`, CCM-R1-WINDOW-LABEL-B). Its presence alone dates a filed label:
   * the backend serves it only on a filing dated after that close, and nothing here compares the
   * two. Board's v3 governance feed carries the same value as a `_filed` record's
   * `refiling_window_closed_on` (CCM-R1-WINDOW-LABEL-C); a host reading the feed passes it here.
   */
  filedAfterWindowClosedOn?: string;
}

/** A filing's label dates, read off its record as the register serves it. */
export function recordLabelDatesOf(obligation: ComplianceObligation | undefined): RecordLabelDates {
  const dates: RecordLabelDates = {};
  if (obligation?.refilingWindowClosedOn !== undefined) {
    dates.windowClosedOn = obligation.refilingWindowClosedOn;
  }
  if (obligation?.filedAfterWindowClosedOn !== undefined) {
    dates.filedAfterWindowClosedOn = obligation.filedAfterWindowClosedOn;
    if (obligation.recordedFiledOn !== undefined) dates.filedOn = obligation.recordedFiledOn;
  }
  return dates;
}

/** The kinds that record a filing with its confirmation: the ones a late filing's label dates. */
const FILED_KINDS: readonly FilingRegisterRecordKind[] = [
  'registration_filing_recorded',
  'exemption_filing_recorded',
  'renewal_filing_recorded',
];

/**
 * The record-shaped label for a composed kind, with the dates it states. Hosts call it as
 * `recordLabel(composeRecordKind(obligation, import), recordLabelDatesOf(obligation))`, Board
 * Governance's governance calendar among them (`CBG-YR1`).
 *
 * ⚠ A CLOSED WINDOW NAMES ITS WINDOW, and says only that Givmo holds no filing for it (Jonathan,
 * 2026-10-01 11:59 PDT, rulings log item 29). A row reaches `window_closed` from a filed state and
 * keeps that earlier filing's date, so an unscoped "no filing recorded" would read as if the filing
 * had never had one; and "in Givmo" keeps the label to what Givmo holds, since a filing made
 * elsewhere and not recorded yet is still a filing. It says when, never "late": what a closed window
 * means is the state's rule's to say. A date this build cannot read shows as a dash (Q-W).
 *
 * ⚠ A FILING RECORDED AFTER ITS WINDOW CLOSED STATES BOTH DATES, and still never "late" (the same
 * ruling's second line, CCM-R1-WINDOW-LABEL-B): "Renewal filing recorded — filed Apr 12, 2026, after
 * the window closed Mar 31, 2026". Only where the close date is served; every other filing keeps its
 * plain label, and a closed window's date never dates one.
 */
export function recordLabel(kind: FilingRegisterRecordKind, dates: RecordLabelDates = {}): string {
  if (kind === 'refiling_window_passed') {
    return `No filing recorded in Givmo for the window that closed ${displayComplianceDate(dates.windowClosedOn)}`;
  }
  if (
    FILED_KINDS.includes(kind) &&
    dates.filedAfterWindowClosedOn !== undefined &&
    dates.filedAfterWindowClosedOn !== ''
  ) {
    return `${RECORD_LABELS[kind]} — filed ${displayComplianceDate(dates.filedOn)}, after the window closed ${displayComplianceDate(dates.filedAfterWindowClosedOn)}`;
  }
  return RECORD_LABELS[kind];
}

/**
 * Whether an obligation belongs in the REGISTER at all.
 *
 * The register holds decisions the organization has made — `confirmed` (it is on our register) and
 * `dismissed` (we looked and decided this does not apply to us). A `proposed` candidate is neither:
 * it is inert (the retired legacy queue left them as history), and letting one lead a register row
 * would put an "Open" affordance and an implied entry against a jurisdiction the organization has
 * not decided anything about. An unrecognized state IS shown, because a row acted on in a way this
 * build cannot describe must not be silently dropped.
 */
export function isRegisterMaterial(obligation: ComplianceObligation): boolean {
  return obligation.candidateState !== 'proposed';
}

/**
 * Format an ISO date for display — the app's own convention, tolerant of null/invalid → em dash.
 *
 * `timeZone: 'UTC'` matters on this surface specifically: every date here is a plain civil date the
 * jurisdiction or the organization stated (a due date, a filing date, a window close), not an
 * instant. Rendering one in the viewer's local zone can shift it a day, and a filing deadline that
 * moves by a day depending on where the reader sits is a false statement about a date. An instant
 * (an upload, a recorded act) takes {@link displayLocalDate} instead: the viewer's own day.
 */
export function displayComplianceDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The day an INSTANT fell on, as the viewer's own calendar reads it, as a civil `YYYY-MM-DD`
 * (CCM-R1-INTAKE-DAY-BASIS): the ONE rule for every instant a Compliance screen dates — an upload,
 * a recorded act, a correction, a request — so a year tile's "Added … on …", a history line and the
 * Documents page's Added column never put one evening's act on two days. Never for a date a
 * jurisdiction or the organization stated, which is a civil date already. `timeZone` names a zone
 * other than the viewer's (tests pin the evening case with it). Unreadable → `undefined`.
 */
export function localDayOf(iso: string | null | undefined, timeZone?: string): string | undefined {
  if (iso === null || iso === undefined || iso === '') return undefined;
  // A civil date has no instant to move: it is its own day wherever the viewer is.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone !== undefined ? { timeZone } : {}),
  }).formatToParts(date);
  const part = (type: 'year' | 'month' | 'day') =>
    parts.find((candidate) => candidate.type === type)?.value;
  const year = part('year');
  const month = part('month');
  const day = part('day');
  return year !== undefined && month !== undefined && day !== undefined
    ? `${year}-${month}-${day}`
    : undefined;
}

/**
 * The day an INSTANT fell on, as the viewer's own calendar reads it ("Sep 26, 2026"), by
 * {@link localDayOf}'s one rule — for when someone did something (a correction, a request), never
 * for a date a jurisdiction or the organization stated, which {@link displayComplianceDate} reads
 * as written. Keeping the viewer's zone is the point: an act recorded on an evening is never dated
 * the next day. Tolerant: null or unreadable → em dash.
 */
export function displayLocalDate(iso: string | null | undefined, timeZone?: string): string {
  const day = localDayOf(iso, timeZone);
  return day === undefined ? '—' : displayComplianceDate(day);
}

/**
 * Build the full 51-row filing register.
 *
 * One row per jurisdiction, whether or not the organization has recorded anything there. Where an
 * organization holds SEVERAL obligations in one jurisdiction (a registration and an annual report,
 * say), the row carries the one whose record is furthest along and the detail surface enumerates the
 * rest — the table answers "what is on file here", and the per-obligation answer lives one click in.
 */
export function buildFilingRegisterEntries(
  obligations: readonly ComplianceObligation[],
  registrationImports: readonly RegistrationImport[],
  occurrences: readonly ComplianceOccurrence[],
): FilingRegisterEntry[] {
  const importsByObligation = new Map<string, RegistrationImport>();
  for (const row of registrationImports) {
    if (row.obligationId.length > 0) importsByObligation.set(row.obligationId, row);
  }

  const nameByCode = new Map<string, string>();
  const obligationsByCode = new Map<string, ComplianceObligation[]>();
  for (const obligation of obligations) {
    const code = obligation.jurisdictionCode;
    if (code.length === 0) continue;
    // A proposed candidate never reaches the register table — see `isRegisterMaterial`. Filtered
    // here rather than at the lead selection so a jurisdiction holding ONLY proposed candidates
    // carries no obligation at all, and therefore no drill-in link implying an entry that does not
    // exist.
    if (!isRegisterMaterial(obligation)) continue;
    // The wire's own name wins over the fallback list wherever it supplies one.
    if (obligation.jurisdictionName.length > 0) nameByCode.set(code, obligation.jurisdictionName);
    const bucket = obligationsByCode.get(code);
    if (bucket === undefined) obligationsByCode.set(code, [obligation]);
    else bucket.push(obligation);
  }

  const nextDueByCode = nextDueDateByJurisdiction(occurrences);

  return US_JURISDICTIONS.map(({ code, name }) => {
    const inJurisdiction = obligationsByCode.get(code) ?? [];
    const obligation = leadObligation(inJurisdiction, importsByObligation);
    const registrationImport =
      obligation === undefined ? undefined : importsByObligation.get(obligation.obligationId);
    const recordKind = composeRecordKind(obligation, registrationImport);

    return {
      jurisdictionCode: code,
      jurisdictionName: nameByCode.get(code) ?? name,
      recordKind,
      recordLabel: recordLabel(recordKind, recordLabelDatesOf(obligation)),
      obligation,
      registrationImport,
      // The typed number belongs ONLY to the awaiting-document arrangement. Attaching it to a
      // receipt-backed row would let a number the organization typed sit where a state's own
      // confirmation belongs.
      recordedRegistrationNumber:
        recordKind === 'imported_awaiting_document'
          ? registrationImport?.recordedRegistrationNumber
          : undefined,
      hasReceiptOnFile:
        obligation !== undefined &&
        isReceiptBacked(obligation.filingRecordState) &&
        obligation.confirmationArtifactId !== undefined,
      // Read from the SAME pair of conditions as `hasReceiptOnFile`, so the row cannot claim a
      // document exists while offering no way to open it, or offer a link on a row that is not
      // receipt-backed.
      confirmationArtifactId:
        obligation !== undefined && isReceiptBacked(obligation.filingRecordState)
          ? obligation.confirmationArtifactId
          : undefined,
      confirmationReference: obligation?.confirmationReference,
      recordedFiledOn: obligation?.recordedFiledOn,
      nextDueOn: nextDueByCode.get(code),
      ruleChangeFlagged: obligation?.ruleChangeFlaggedOn !== undefined,
    };
  });
}

/**
 * How far along a record is, for choosing which obligation leads a jurisdiction's row.
 *
 * Ordering by "furthest along" rather than by recency: a jurisdiction where one filing has a receipt
 * on file and another has nothing should lead with the receipt, because that is the row's most
 * specific true statement. `declined` ranks below `nothing_recorded` deliberately — an organization
 * that declined one rule and confirmed another has confirmed something, and the row should say so.
 */
const RECORD_KIND_RANK: Record<FilingRegisterRecordKind, number> = {
  registration_filing_recorded: 7,
  exemption_filing_recorded: 7,
  renewal_filing_recorded: 7,
  submission_recorded: 6,
  imported_awaiting_document: 5,
  refiling_window_passed: 4,
  no_filing_recorded: 3,
  unrecognized: 2,
  declined: 1,
  nothing_recorded: 0,
};

function leadObligation(
  candidates: readonly ComplianceObligation[],
  importsByObligation: ReadonlyMap<string, RegistrationImport>,
): ComplianceObligation | undefined {
  let lead: ComplianceObligation | undefined;
  let leadRank = -1;
  for (const obligation of candidates) {
    const kind = composeRecordKind(obligation, importsByObligation.get(obligation.obligationId));
    const rank = RECORD_KIND_RANK[kind];
    if (rank > leadRank) {
      lead = obligation;
      leadRank = rank;
    }
  }
  return lead;
}

/**
 * The soonest computable date per jurisdiction.
 *
 * ⚠ Only a real `due_on` counts. A `cadence_prompt_on` is the organization's own recorded year end
 * for a cycle the jurisdiction publishes NO date for — rendering it in a "next date" column would
 * assert a deadline no source states. It is surfaced on the deadlines surface, labelled as what it
 * is, and never here.
 */
export function nextDueDateByJurisdiction(
  occurrences: readonly ComplianceOccurrence[],
): Map<string, string> {
  const soonest = new Map<string, string>();
  for (const occurrence of occurrences) {
    const due = occurrence.dueOn;
    if (due === undefined || occurrence.jurisdictionCode.length === 0) continue;
    const current = soonest.get(occurrence.jurisdictionCode);
    if (current === undefined || due < current) soonest.set(occurrence.jurisdictionCode, due);
  }
  return soonest;
}

// ── Cycle timing — the three kinds of "no date" ────────────────────────────────────────────────

/** What a cycle's timing actually is. */
export type CycleTimingKind =
  | 'dated'
  | 'before_first_solicitation'
  | 'recurrence_without_published_date'
  | 'anchor_missing';

export interface CycleTiming {
  kind: CycleTimingKind;
  /** The computed date, on a `dated` cycle only. */
  dueOn?: string;
  /** The organization's own recorded cycle turnover. NEVER a due date. */
  cadencePromptOn?: string;
  /** Which anchor the engine could not place, on an `anchor_missing` cycle. */
  uncomputableReason?: string;
}

/**
 * Separate the three kinds of "no date".
 *
 * ⚠ THE NULLS CANNOT TELL THEM APART, AND `due_date_basis` IS THE ONLY THING THAT CAN.
 *
 *   1. `before_first_solicitation` computes to nothing BY NATURE — the absence IS the answer, and the
 *      duty is a one-time pre-solicitation one.
 *   2. `not_stated_in_source` means the jurisdiction states a RECURRENCE and publishes no date. It
 *      carries a NULL `uncomputable_reason` too, so a consumer reading the nulls instead of the basis
 *      will prompt annually about a one-time duty — or, worse, stay silent about an annual one.
 *   3. A missing anchor is the third: the date exists and the engine could not place it, and the
 *      reason names the anchor that is absent.
 *
 * Collapsing any pair would let a real gap render as "nothing is due".
 */
export function describeCycleTiming(occurrence: ComplianceOccurrence): CycleTiming {
  if (occurrence.dueOn !== undefined) {
    return { kind: 'dated', dueOn: occurrence.dueOn };
  }
  if (occurrence.uncomputableReason !== undefined) {
    return { kind: 'anchor_missing', uncomputableReason: occurrence.uncomputableReason };
  }
  if (occurrence.dueDateBasis === 'before_first_solicitation') {
    return { kind: 'before_first_solicitation' };
  }
  if (occurrence.dueDateBasis === 'not_stated_in_source') {
    return {
      kind: 'recurrence_without_published_date',
      cadencePromptOn: occurrence.cadencePromptOn,
    };
  }
  // A basis this build does not recognize with no date and no reason. The honest reading is that the
  // engine placed no date and this version cannot say why — never "nothing is due".
  return { kind: 'anchor_missing', uncomputableReason: occurrence.uncomputableReason };
}

/** Cycles that carry a real computed date, soonest first — what the deadlines surface lists. */
export function datedOccurrences(
  occurrences: readonly ComplianceOccurrence[],
): ComplianceOccurrence[] {
  return occurrences
    .filter((o) => o.dueOn !== undefined)
    .sort((a, b) => (a.dueOn ?? '').localeCompare(b.dueOn ?? ''));
}

/**
 * Group dated cycles by their date.
 *
 * The May-15 stack is the shape this exists for: a dozen jurisdictions genuinely share one date, and
 * a list that rendered them as twelve separate rows would understate the day while a count that
 * hid the jurisdictions would understate the work. Both are shown.
 */
export function groupOccurrencesByDate(
  occurrences: readonly ComplianceOccurrence[],
): { dueOn: string; occurrences: ComplianceOccurrence[] }[] {
  const byDate = new Map<string, ComplianceOccurrence[]>();
  for (const occurrence of datedOccurrences(occurrences)) {
    const due = occurrence.dueOn ?? '';
    const bucket = byDate.get(due);
    if (bucket === undefined) byDate.set(due, [occurrence]);
    else bucket.push(occurrence);
  }
  return [...byDate.entries()]
    .map(([dueOn, rows]) => ({ dueOn, occurrences: rows }))
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
}

/** Cycles with no computed date, which the deadlines surface reports rather than hides. */
export function undatedOccurrences(
  occurrences: readonly ComplianceOccurrence[],
): ComplianceOccurrence[] {
  return occurrences.filter((o) => o.dueOn === undefined);
}

// ── The recordable parameters, and how each one's value is shaped ──────────────────────────────

/** The typed shape a parameter's value takes. One kind per key, CHECKed server-side. */
export type ProfileValueKind =
  | 'month_day'
  | 'jurisdiction_code'
  | 'jurisdiction_code_list'
  | 'money_usd'
  | 'boolean'
  | 'classification_code';

export interface ProfileParameterSpec {
  /** A key the recording body takes: the profile records neither registered agents nor foreign
   *  qualifications, which are the organization's vendor records (CCM-MOD5-ONE-STORE). */
  key: ComplianceProfileParameterRecord['parameter_key'];
  /** VERBATIM from the backend — see the warning on {@link PROFILE_PARAMETER_SPECS}. */
  label: string;
  kind: ProfileValueKind;
  /** Whether the registry refuses to read the profile at all without it. */
  required: boolean;
  /** One line saying what the organization is being asked, in its own terms. */
  help: string;
}

/**
 * Every fact the organization can record about itself, in the order the form asks for them.
 *
 * ⚠ **THE LABELS ARE THE BACKEND'S, VERBATIM.** They are copied from
 * `COMPLIANCE_PARAMETER_LABELS` / `PROFILE_PARAMETER_LABELS`, and the backend keeps ITS two label
 * maps in one place for exactly this reason: "against your recorded fiscal year end (Dec 31), this
 * comes out at May 15" is only honest if the label the organization typed under and the label the
 * computation renders came from the same string. A second spelling here would drift and quietly
 * break that. The wire supplies `parameter_label` on every recorded row and that always wins on
 * READ; this list exists only for keys with nothing recorded yet, which have no served label.
 *
 * ⚠ There is deliberately NO key that answers "must we register" or "are we exempt". These are
 * facts, and the module holds no conclusion for them to feed.
 */
export const PROFILE_PARAMETER_SPECS: readonly ProfileParameterSpec[] = [
  {
    key: 'fiscal_year_end',
    label: 'Fiscal year end',
    kind: 'month_day',
    required: true,
    help: 'The anchor every year-end-relative date is worked out against.',
  },
  {
    key: 'state_of_incorporation',
    label: 'State of incorporation',
    kind: 'jurisdiction_code',
    required: false,
    help: 'Where the organization is incorporated.',
  },
  {
    key: 'solicitation_jurisdictions',
    label: 'Jurisdictions where the organization records soliciting',
    kind: 'jurisdiction_code_list',
    required: false,
    help: 'Each one you name is looked up in the rules library.',
  },
  {
    key: 'solicits_nationwide_or_online',
    label: 'Solicits nationwide or online',
    kind: 'boolean',
    required: false,
    help: 'Answering yes proposes candidates in jurisdictions you did not name; they are listed separately for review.',
  },
  {
    key: 'recorded_revenue',
    label: 'Gross revenue the organization recorded',
    kind: 'money_usd',
    required: false,
    help: 'Your own figure. It is recorded, not checked against any threshold.',
  },
  {
    key: 'engages_paid_fundraiser',
    label: 'Engages a paid fundraiser, solicitor or fundraising consultant',
    kind: 'boolean',
    required: false,
    help: 'A fact about how the organization raises funds.',
  },
  {
    key: 'irs_classification',
    label: 'IRS classification the organization records',
    kind: 'classification_code',
    required: false,
    help: 'For example 501(c)(3).',
  },
];

/** Look one spec up by key, for rendering a `missing_required_parameter_keys` entry. */
export function profileParameterSpec(key: string): ProfileParameterSpec | undefined {
  return PROFILE_PARAMETER_SPECS.find((spec) => spec.key === key);
}

/** What a form collects before it is shaped into the wire's typed value. */
export interface ParameterInput {
  month?: string;
  day?: string;
  code?: string;
  codes?: string[];
  /** Money as the user typed it — a STRING, all the way to the wire. */
  amount?: string;
  flag?: boolean;
}

/**
 * Shape one form input into the wire's typed value object.
 *
 * Returns `undefined` when the input is not answerable yet, so a caller cannot send a half-value.
 *
 * ⚠ **MONEY IS A STRING END TO END.** `amount_usd` is passed through exactly as typed and is never
 * parsed to a number — repo law, and it matters here because a revenue figure the organization
 * recorded is a fact it asserted, and a float round-trip would make the register report a number
 * nobody gave.
 */
export function buildParameterValue(
  kind: ProfileValueKind,
  input: ParameterInput,
): Record<string, unknown> | undefined {
  switch (kind) {
    case 'month_day': {
      const month = Number(input.month);
      const day = Number(input.day);
      if (!Number.isInteger(month) || month < 1 || month > 12) return undefined;
      if (!Number.isInteger(day) || day < 1 || day > 31) return undefined;
      return { fiscal_year_end_month: month, fiscal_year_end_day: day };
    }
    case 'jurisdiction_code': {
      const code = input.code ?? '';
      return code.length === 2 ? { jurisdiction_code: code } : undefined;
    }
    case 'jurisdiction_code_list': {
      const codes = input.codes ?? [];
      return codes.length > 0 ? { jurisdiction_codes: codes } : undefined;
    }
    case 'money_usd': {
      const amount = (input.amount ?? '').trim();
      // Digits with at most two decimal places, and nothing else — the wire takes a Decimal, and a
      // value it refuses is better caught here than as a 422.
      return /^\d+(\.\d{1,2})?$/.test(amount) ? { amount_usd: amount } : undefined;
    }
    case 'boolean':
      // ⚠ `undefined` is NOT `false`. An unanswered yes/no must not record a "no" the organization
      // never gave — especially `solicits_nationwide_or_online`, where a defaulted answer would
      // silently decide the size of the register.
      return input.flag === undefined ? undefined : { flag_value: input.flag };
    case 'classification_code': {
      const code = (input.code ?? '').trim();
      return code.length > 0 ? { classification_code: code } : undefined;
    }
  }
}

// ── Error shapes the surfaces render rather than report ────────────────────────────────────────

/**
 * Whether an error means "this module does not exist in this environment".
 *
 * The backend's two layered gates — `CHARITY_PORTAL_ENABLED` then
 * `CHARITY_COMPLIANCE_MODULE_ENABLED` — both raise the SAME exception mapped to a 404, deliberately:
 * a caller must not be able to tell "the portal is off" from "the compliance module is off" by the
 * shape of the answer, and a dark route is indistinguishable from one that was never deployed.
 *
 * So this is a state the console renders neutrally, never an error it reports. No toast, no error
 * boundary, no retry — the module simply says it is not available.
 */
export function isModuleUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/**
 * Whether the vault's storage is not configured in this environment.
 *
 * ⚠ **A 503 HERE IS A FAIL-SAFE, NOT AN OUTAGE, AND THE COPY MUST SAY SO.** The backend refuses to
 * fall back to the public default bucket or to either 365-day-lifecycle bucket, because a
 * confirmation letter silently deleted a year later would leave the register asserting a filing
 * whose document is gone. So the honest rendering is "documents can't be recorded in this
 * environment yet" — never "something went wrong", which would invite a retry that cannot succeed,
 * and never anything implying the organization's existing records are affected. They are not: the
 * register, the recorded facts and the computed dates are all unaffected by the bucket's absence.
 */
export function isVaultStorageUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 503;
}

/**
 * The `error_code` the backend puts on a Word file it refused at confirm (CCM-R1-INTAKE-WORD): a
 * `.docm` whose macros it could not remove, so the file was not kept.
 */
export const WORD_REFUSED_ERROR_CODE = 'COMPLIANCE_WORD_REFUSED';

/**
 * CCM-BE24-B (D-CCM-74): a fiscal year end refused because it moved under filings that count from it
 * without the organization's answer. The refusal's own words (Y7) are the API's and the connected AI's
 * only: a screen meeting it re-reads the profile, which then asks Y1 where the year end is chosen, and
 * never shows them (cut 1008-42's review, F4).
 */
export const YEAR_END_CHANGE_UNANSWERED_ERROR_CODE = 'COMPLIANCE_YEAR_END_CHANGE_UNANSWERED';

/** Whether a fiscal year end save met {@link YEAR_END_CHANGE_UNANSWERED_ERROR_CODE}. Keyed on the code. */
export function isYearEndChangeUnanswered(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === YEAR_END_CHANGE_UNANSWERED_ERROR_CODE;
}

/**
 * Whether a confirm was refused because a Word file's macros could not be removed.
 *
 * ⚠ CHECK IT BEFORE {@link isUploadIncomplete}, WHICH ALSO MATCHES IT. Both are 400s on the confirm
 * step and both mean nothing was recorded, but here the upload DID finish: the file was read and
 * refused, and the same file is refused every time, so "try again" would be untrue. The surface shows
 * the backend's own line instead, which says why and what to upload. Keyed on the envelope's
 * `error_code`, never on the line's text.
 */
export function isWordRefused(error: unknown): boolean {
  return (
    isGivmoError(error) && error.status === 400 && firstErrorCode(error) === WORD_REFUSED_ERROR_CODE
  );
}

/**
 * Whether a confirm was refused because the upload never finished.
 *
 * ⚠ A 400 on the confirm step means NO ROW WAS WRITTEN, and that is the property worth surfacing
 * honestly: recording a row for an upload that did not complete would mint an artifact id resolving
 * to nothing, and that id's entire purpose is to be what a receipt-backed filing points at. The
 * surface says the document did not finish uploading and offers the upload again — it must never
 * report a partial success. A Word file refused at confirm is a 400 too, and is not an unfinished
 * upload: {@link isWordRefused} is checked first.
 */
export function isUploadIncomplete(error: unknown): boolean {
  return isGivmoError(error) && error.status === 400;
}

/**
 * Whether a delete was refused because something in the register cites the document.
 *
 * ⚠ **SCOPED TO THE DELETE CALL, AND ONLY THERE.** A 409 is the module's general "well-formed
 * request refused on state" answer, so the status alone does not identify the refusal. What
 * disambiguates it is the call it came from, which is why this is used at exactly one mutation
 * rather than as a general classifier.
 *
 * Reaching it at all means the list said deletable and the wire disagreed — a race with a filing
 * recorded in another tab. The refusal is permanent, so the surface states that and re-reads the
 * list rather than offering a retry.
 */
export function isArtifactCited(error: unknown): boolean {
  return isGivmoError(error) && error.status === 409;
}

/**
 * A signing act the server refused because the request moved on: any 409 (cut 1006-7's B1–B8 and an
 * issue's or a revision's own refusals). The page around the served line reads the request again
 * (CCM-R1-SIGN-WEB-C's N2, on the signer's side and the sender's alike).
 */
export function isActConflict(error: unknown): boolean {
  return isGivmoError(error) && error.status === 409;
}

/**
 * Whether a desk act was refused because the organization has Givmo's AI turned off (R2, D-CCM-67):
 * the hand-over answers `COMPLIANCE_GIVMO_AI_OFF`, and the page opens frame 21 or 21b instead of a
 * failure line (CCM-R1-DESK-WEB). Keyed on the envelope's `error_code`.
 */
export function isGivmoAiOff(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === 'COMPLIANCE_GIVMO_AI_OFF';
}

/** The server's own message where it gave one, and the caller's fallback otherwise. */
export function serverMessage(error: unknown, fallback: string): string {
  return isGivmoError(error) && error.message.trim().length > 0 ? error.message : fallback;
}
